import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import TravaFetcher from "../scoring-fetcher/trava-fetcher";
import { POOL_IDS, TravaBscPool, TravaEthPool, TravaFtmPool } from "src/config/poolsConfig";
import { NETWORK } from "src/config/constants";

const PoolsContext = React.createContext({});

const PoolsProvider = ({ children }) => {
  const [bscTravaToken, setBscTravaToken] = useState(null);
  const [ftmTravaToken, setFtmTravaToken] = useState(null);
  const [ethTravaToken, setEthTravaToken] = useState(null);

  const { bscProvider, ftmProvider, ethProvider } = useProviderContext();

  const { travaBscFetcher } = useMemo(() => {
    if (bscProvider) {
      const { poolAddress, incentiveAddress, oraclePrice } = TravaBscPool.metadata;
      const travaBscFetcher = new TravaFetcher(
        poolAddress,
        incentiveAddress,
        oraclePrice,
        bscProvider,
        NETWORK.BSC_MAINNET
      );
      return { travaBscFetcher };
    }
    return { travaBscFetcher: null };
  }, [bscProvider]);

  const { travaFtmFetcher } = useMemo(() => {
    if (ftmProvider) {
      const { poolAddress, incentiveAddress, oraclePrice } = TravaFtmPool.metadata;
      const travaFtmFetcher = new TravaFetcher(
        poolAddress,
        incentiveAddress,
        oraclePrice,
        ftmProvider,
        NETWORK.FTM_MAINNET
      );
      return { travaFtmFetcher };
    }
    return { travaFtmFetcher: null };
  }, [ftmProvider]);

  const { travaEthFetcher } = useMemo(() => {
    if (ethProvider) {
      const { poolAddress, incentiveAddress, oraclePrice } = TravaEthPool.metadata;
      const travaEthFetcher = new TravaFetcher(
        poolAddress,
        incentiveAddress,
        oraclePrice,
        ethProvider,
        NETWORK.ETH_MAINNET
      );
      return { travaEthFetcher };
    }
    return { travaEthFetcher: null };
  }, [ethProvider]);

  const fetchTravaBscToken = useCallback(async () => {
    if (travaBscFetcher) {
      const _data = await travaBscFetcher.getTokenData();
      setBscTravaToken(_data);
    }
  }, [travaBscFetcher]);

  const fetchTravaFtmToken = useCallback(async () => {
    if (travaFtmFetcher) {
      const _data = await travaFtmFetcher.getTokenData();
      setFtmTravaToken(_data);
    }
  }, [travaFtmFetcher]);

  const fetchTravaEthToken = useCallback(async () => {
    if (travaEthFetcher) {
      const _data = await travaEthFetcher.getTokenData();
      setEthTravaToken(_data);
    }
  }, [travaEthFetcher]);

  const contextData = useMemo(
    () => ({
      tokens: {
        bscTravaToken,
        ftmTravaToken,
        ethTravaToken,
      },
      fetch: {
        fetchTravaBscToken,
        fetchTravaFtmToken,
        fetchTravaEthToken,
      },
      fetchTravaPrice: {
        bsc: travaBscFetcher?.getOraclePrice,
        ftm: travaFtmFetcher?.getOraclePrice,
        eth: travaEthFetcher?.getOraclePrice,
      },
    }),
    [bscTravaToken, ftmTravaToken, ethTravaToken, fetchTravaBscToken, fetchTravaFtmToken, fetchTravaEthToken]
  );

  return <PoolsContext.Provider value={contextData}>{children}</PoolsContext.Provider>;
};

PoolsProvider.propTypes = {
  children: PropTypes.node,
};

export default PoolsProvider;

export const usePoolsContext = () => React.useContext(PoolsContext);

export const usePoolsSelector = (selectorFn) => {
  const data = usePoolsContext();
  return useMemo(() => selectorFn(data), [data, selectorFn]);
};

export const useParticularPoolsSelector = (pool) => {
  const data = usePoolsContext();
  const tokens = data.tokens;
  const fetch = data.fetch;
  const fetchTravaPrice = data.fetchTravaPrice;
  return useMemo(() => {
    if (pool === POOL_IDS.TRAVA_BSC)
      return {
        token: tokens.bscTravaToken,
        fetch: fetch.fetchTravaBscToken,
        fetchTravaPrice: fetchTravaPrice.bsc,
      };
    else if (pool === POOL_IDS.TRAVA_FTM)
      return {
        token: tokens.ftmTravaToken,
        fetch: fetch.fetchTravaFtmToken,
        fetchTravaPrice: fetchTravaPrice.ftm,
      };
    else if (pool === POOL_IDS.TRAVA_ETH)
      return {
        token: tokens.ethTravaToken,
        fetch: fetch.fetchTravaEthToken,
        fetchTravaPrice: fetchTravaPrice.eth,
      };
    else throw Error("not found pool ids");
  }, [data, pool]);
};
